import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Settings, Plus, Edit2, ExternalLink, Loader2, RefreshCw } from 'lucide-react';
import { api } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const AdminLegalPage = () => {
  const [activeTab, setActiveTab] = useState('documents');

  // ----- Documents tab state -----
  const [documents, setDocuments] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(true);
  const [showNewDocForm, setShowNewDocForm] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [docForm, setDocForm] = useState({ slug: '', title: '', content: '' });
  const [savingDoc, setSavingDoc] = useState(false);

  // ----- Platform config tab state -----
  const [config, setConfig] = useState(null);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [savingConfig, setSavingConfig] = useState(false);

  const fetchDocuments = useCallback(async () => {
    setLoadingDocs(true);
    try {
      const res = await api.get('/legal/admin/documents');
      setDocuments(res.data);
    } catch (error) {
      toast.error('Errore nel caricamento documenti');
    } finally {
      setLoadingDocs(false);
    }
  }, []);

  const fetchConfig = useCallback(async () => {
    setLoadingConfig(true);
    try {
      const res = await api.get('/legal/admin/platform-config');
      setConfig(res.data);
    } catch (error) {
      toast.error('Errore nel caricamento configurazione');
    } finally {
      setLoadingConfig(false);
    }
  }, []);

  useEffect(() => {
    fetchDocuments();
    fetchConfig();
  }, [fetchDocuments, fetchConfig]);

  // Group documents by slug, show only the active one per slug
  const docsBySlug = documents.reduce((acc, doc) => {
    if (!acc[doc.slug]) acc[doc.slug] = [];
    acc[doc.slug].push(doc);
    return acc;
  }, {});

  const handleNewDoc = () => {
    setEditingDoc(null);
    setDocForm({ slug: '', title: '', content: '' });
    setShowNewDocForm(true);
  };

  const handleEditDoc = (doc) => {
    setEditingDoc(doc);
    setDocForm({ slug: doc.slug, title: doc.title, content: doc.content });
    setShowNewDocForm(true);
  };

  const handleSaveDoc = async (e) => {
    e.preventDefault();
    if (!docForm.slug || !docForm.title || !docForm.content) {
      toast.error('Tutti i campi sono obbligatori');
      return;
    }

    setSavingDoc(true);
    try {
      if (editingDoc) {
        await api.put(`/legal/admin/documents/${editingDoc.id}`, {
          title: docForm.title,
          content: docForm.content,
        });
        toast.success('Documento aggiornato');
      } else {
        await api.post('/legal/admin/documents', docForm);
        toast.success('Nuova versione creata');
      }
      setShowNewDocForm(false);
      fetchDocuments();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Errore nel salvataggio';
      toast.error(msg);
    } finally {
      setSavingDoc(false);
    }
  };

  const handleSaveConfig = async (e) => {
    e.preventDefault();
    setSavingConfig(true);
    try {
      await api.put('/legal/admin/platform-config', config);
      toast.success('Configurazione salvata');
    } catch (error) {
      toast.error('Errore nel salvataggio configurazione');
    } finally {
      setSavingConfig(false);
    }
  };

  const SLUG_LABELS = {
    'termini-condizioni': 'Termini e Condizioni',
    'privacy-policy': 'Privacy Policy',
    'cookie-policy': 'Cookie Policy',
    'resi-rimborsi': 'Politica Resi e Rimborsi',
    'termini-vendor': 'Termini Vendor',
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-2xl font-bold text-text-primary">Documenti Legali</h1>
          <p className="text-text-secondary text-sm mt-1">Gestisci termini, privacy e configurazione legale</p>
        </div>
        <Link to="/admin">
          <Button variant="outline" size="sm">← Admin</Button>
        </Link>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-lg p-1 mb-8 w-fit">
        {[
          { id: 'documents', label: 'Documenti', icon: FileText },
          { id: 'config', label: 'Configurazione Legale', icon: Settings },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? 'bg-white shadow text-text-primary'
                : 'text-text-secondary hover:text-text-primary'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* --- TAB: DOCUMENTS --- */}
      {activeTab === 'documents' && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-text-primary">Documenti attivi</h2>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={fetchDocuments}>
                <RefreshCw className="w-4 h-4" />
              </Button>
              <Button size="sm" onClick={handleNewDoc}>
                <Plus className="w-4 h-4 mr-1" /> Nuovo documento / versione
              </Button>
            </div>
          </div>

          {/* New/Edit form */}
          {showNewDocForm && (
            <div className="bg-white border border-border rounded-xl p-6 mb-6">
              <h3 className="font-semibold mb-4">
                {editingDoc ? `Modifica: ${editingDoc.title} (v${editingDoc.version})` : 'Nuovo documento / nuova versione'}
              </h3>
              <form onSubmit={handleSaveDoc} className="space-y-4">
                {!editingDoc && (
                  <div>
                    <Label htmlFor="doc-slug">Slug (es. termini-condizioni)</Label>
                    <Input
                      id="doc-slug"
                      value={docForm.slug}
                      onChange={e => setDocForm({ ...docForm, slug: e.target.value })}
                      placeholder="termini-condizioni"
                      className="mt-1"
                      required
                    />
                    <p className="text-xs text-text-muted mt-1">
                      Se lo slug esiste, verrà creata una nuova versione e la precedente verrà disattivata.
                    </p>
                  </div>
                )}
                <div>
                  <Label htmlFor="doc-title">Titolo</Label>
                  <Input
                    id="doc-title"
                    value={docForm.title}
                    onChange={e => setDocForm({ ...docForm, title: e.target.value })}
                    placeholder="Termini e Condizioni"
                    className="mt-1"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="doc-content">Contenuto</Label>
                  <Textarea
                    id="doc-content"
                    value={docForm.content}
                    onChange={e => setDocForm({ ...docForm, content: e.target.value })}
                    rows={12}
                    className="mt-1 font-mono text-sm"
                    required
                  />
                </div>
                <div className="flex gap-2">
                  <Button type="submit" disabled={savingDoc}>
                    {savingDoc ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Salvataggio...</> : 'Salva'}
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setShowNewDocForm(false)}>
                    Annulla
                  </Button>
                </div>
              </form>
            </div>
          )}

          {/* Documents list */}
          {loadingDocs ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-text-muted" />
            </div>
          ) : (
            <div className="space-y-3">
              {Object.entries(docsBySlug).map(([slug, versions]) => {
                const activeDoc = versions.find(d => d.is_active);
                const doc = activeDoc || versions[0];
                return (
                  <div key={slug} className="bg-white border border-border rounded-xl p-5">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-text-primary">{doc.title}</h3>
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                            doc.is_active
                              ? 'bg-green-100 text-green-700'
                              : 'bg-gray-100 text-gray-500'
                          }`}>
                            v{doc.version}
                          </span>
                          {doc.is_active && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary font-medium">
                              Attivo
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-text-muted mt-1">
                          Slug: <code className="bg-gray-100 px-1 rounded">{slug}</code>
                          {doc.updated_at && (
                            <> · Aggiornato {new Date(doc.updated_at).toLocaleDateString('it-IT')}</>
                          )}
                          {!doc.updated_at && doc.created_at && (
                            <> · Creato {new Date(doc.created_at).toLocaleDateString('it-IT')}</>
                          )}
                          {versions.length > 1 && (
                            <> · {versions.length} versioni totali</>
                          )}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <a href={`/legal/${slug}`} target="_blank" rel="noreferrer">
                          <Button variant="outline" size="sm">
                            <ExternalLink className="w-4 h-4" />
                          </Button>
                        </a>
                        {doc.is_active && (
                          <Button variant="outline" size="sm" onClick={() => handleEditDoc(doc)}>
                            <Edit2 className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setDocForm({ slug, title: doc.title, content: doc.content });
                            setEditingDoc(null);
                            setShowNewDocForm(true);
                          }}
                          title="Crea nuova versione"
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <p className="text-sm text-text-secondary mt-3 line-clamp-2">{doc.content}</p>
                  </div>
                );
              })}

              {Object.keys(docsBySlug).length === 0 && (
                <div className="text-center py-12 text-text-muted">
                  Nessun documento. Crea il primo documento legale.
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* --- TAB: PLATFORM CONFIG --- */}
      {activeTab === 'config' && (
        <div className="bg-white border border-border rounded-xl p-6">
          <h2 className="font-semibold text-text-primary mb-6">Configurazione legale piattaforma</h2>

          {loadingConfig ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-text-muted" />
            </div>
          ) : config ? (
            <form onSubmit={handleSaveConfig} className="space-y-5">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="cfg-brand">Nome piattaforma</Label>
                  <Input
                    id="cfg-brand"
                    value={config.platform_name || ''}
                    onChange={e => setConfig({ ...config, platform_name: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="cfg-support">Email supporto</Label>
                  <Input
                    id="cfg-support"
                    type="email"
                    value={config.support_email || ''}
                    onChange={e => setConfig({ ...config, support_email: e.target.value })}
                    className="mt-1"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="cfg-entity">Soggetto legale (ragione sociale)</Label>
                <Input
                  id="cfg-entity"
                  value={config.legal_entity_name || ''}
                  onChange={e => setConfig({ ...config, legal_entity_name: e.target.value })}
                  placeholder="es. DalSud S.r.l."
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="cfg-address">Indirizzo sede legale</Label>
                <Input
                  id="cfg-address"
                  value={config.legal_address || ''}
                  onChange={e => setConfig({ ...config, legal_address: e.target.value })}
                  placeholder="Via Roma 1, 80100 Napoli (NA)"
                  className="mt-1"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="cfg-vat">Partita IVA</Label>
                  <Input
                    id="cfg-vat"
                    value={config.vat_number || ''}
                    onChange={e => setConfig({ ...config, vat_number: e.target.value })}
                    placeholder="IT00000000000"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="cfg-legal-email">Email legale / PEC</Label>
                  <Input
                    id="cfg-legal-email"
                    type="email"
                    value={config.legal_email || ''}
                    onChange={e => setConfig({ ...config, legal_email: e.target.value })}
                    placeholder="legal@dalsud.it"
                    className="mt-1"
                  />
                </div>
              </div>
              {config.updated_at && (
                <p className="text-xs text-text-muted">
                  Ultima modifica: {new Date(config.updated_at).toLocaleString('it-IT')}
                </p>
              )}
              <Button type="submit" disabled={savingConfig}>
                {savingConfig ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Salvataggio...</>
                ) : (
                  'Salva configurazione'
                )}
              </Button>
            </form>
          ) : (
            <p className="text-text-muted">Configurazione non disponibile.</p>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminLegalPage;
